/**
 * Shopping list. Pure functions over plan-data.json plus stored state.
 *
 * Ingredient quantities are parsed from each meal's `d` string, which is the only
 * source of plan content. Nothing here holds a hardcoded ingredient, quantity or
 * synonym table:
 *
 *  - Aggregation is by exact (lowercased) name, so "reduced-salt soy sauce" and
 *    "reduced-salt soy" stay two lines. Merging them would need a synonym list, which
 *    would be invented plan content.
 *  - "cooked basmati" is reported verbatim as a cooked weight. Converting to a dry
 *    purchase weight would need a conversion factor that is not in the data, and a
 *    wrong one would silently move the whole plan's carbohydrate figure.
 *  - A fragment with no leading quantity is surfaced, never dropped. Some are real
 *    ingredients (lemon, garlic) and some are preparation notes ("drained"); no rule
 *    separates them reliably, so the user hides the ones they do not want.
 */
import { PLAN } from './data.ts';
import type { FoodItem, PlanState, SlotName } from './types.ts';
import { slotsFor, mealFor, plannedMealFor, isCustom } from './nutrition.ts';

export const MIN_PEOPLE = 1;
export const MAX_PEOPLE = 8;
export const DEFAULT_PEOPLE = 1;

/** A leading quantity: "250g oats", "5ml olive oil", "1 tbsp tahini". */
const QTY = /^(\d+(?:\.\d+)?)\s*(g|ml|tbsp|tsp)\b\s*(.*)$/i;
/** A leading count: "3 eggs (150g)" -> 3 eggs. You buy eggs by number, not by gram. */
const COUNT = /^(\d+)\s+(.+)$/;
/** A parenthesised gram note after a count, which is nutrition data, not a purchase unit. */
const GRAM_NOTE = /\s*\((?:about\s*)?\d+(?:\.\d+)?\s*g\)\s*$/i;

export interface ParsedFragment {
  qty: number | null;      // null when the fragment carries no leading quantity
  unit: string;            // 'g' | 'ml' | 'tbsp' | 'tsp' | '' for counted or unquantified
  name: string;
  raw: string;
}

export function parseFragment(fragment: string): ParsedFragment {
  const raw = fragment.trim().replace(/\.$/, '').trim();
  const q = QTY.exec(raw);
  if (q) return { qty: Number(q[1]), unit: q[2].toLowerCase(), name: q[3].trim(), raw };
  const c = COUNT.exec(raw);
  if (c) return { qty: Number(c[1]), unit: '', name: c[2].replace(GRAM_NOTE, '').trim(), raw };
  return { qty: null, unit: '', name: raw, raw };
}

/** Stable key for tick and hide state. Must never contain ':', which separates key parts. */
export function slugify(name: string): string {
  return name.toLowerCase().normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'item';
}

export interface ShoppingLine {
  slug: string;
  name: string;
  unit: string;
  /** Already multiplied by the number of people. Null for unquantified lines. */
  qty: number | null;
  /** Before scaling, so the UI can show "×2" honestly. */
  baseQty: number | null;
  /** Which meals it came from, for "why is this on my list". */
  meals: string[];
  /** A cooked weight is not a purchase weight. */
  cooked: boolean;
}

export interface ShoppingList {
  quantified: ShoppingLine[];
  unquantified: ShoppingLine[];
  hidden: ShoppingLine[];
  days: number[];
  people: number;
  mealCount: number;
  cookedLines: ShoppingLine[];
  /** Meals you logged yourself. They have no ingredient list, and you should not buy
   *  food for a meal you already replaced, so they contribute nothing to the list. */
  skippedCustom: Array<{ day: number; slot: SlotName; name: string }>;
}

const COOKED = /\bcooked\b/i;

/**
 * Aggregates every ingredient across the selected days.
 * Uses mealFor(), so a stored swap changes the list exactly as it changes the day.
 */
export function buildShoppingList(
  state: PlanState, days: readonly number[], people: number,
): ShoppingList {
  const n = clampPeople(people);
  const uniqueDays = [...new Set(days)].filter((d) => Number.isInteger(d) && d >= 0 && d <= 111)
    .sort((a, b) => a - b);

  const byKey = new Map<string, { line: ShoppingLine; meals: Set<string> }>();
  const skippedCustom: ShoppingList['skippedCustom'] = [];
  let mealCount = 0;

  for (const day of uniqueDays) {
    for (const slot of slotsFor(state, day) as SlotName[]) {
      // A meal you logged yourself replaces the planned one, so neither belongs on the
      // shopping list: the logged meal has no ingredients, and the planned meal is not
      // being cooked.
      if (isCustom(state, day, slot)) {
        skippedCustom.push({ day, slot, name: mealFor(state, day, slot).n });
        continue;
      }
      const meal: FoodItem = mealFor(state, day, slot);
      mealCount++;
      for (const fragment of meal.d.split(',')) {
        const p = parseFragment(fragment);
        if (!p.name) continue;
        // Key on name AND unit: 100g of something is not 100ml of it.
        const key = `${p.name.toLowerCase()}|${p.unit}|${p.qty === null ? 'u' : 'q'}`;
        const found = byKey.get(key);
        if (found) {
          if (p.qty !== null && found.line.baseQty !== null) found.line.baseQty += p.qty;
          found.meals.add(meal.n);
        } else {
          byKey.set(key, {
            line: {
              slug: slugify(p.name), name: p.name, unit: p.unit,
              qty: null, baseQty: p.qty, meals: [], cooked: COOKED.test(p.name),
            },
            meals: new Set([meal.n]),
          });
        }
      }
    }
  }

  const all: ShoppingLine[] = [...byKey.values()].map(({ line, meals }) => ({
    ...line,
    meals: [...meals].sort(),
    // Unquantified lines are not scaled: "lemon" times two people is not a number.
    qty: line.baseQty === null ? null : round(line.baseQty * n),
  }));

  const visible = all.filter((l) => state.get(`shopHide:${l.slug}`) !== 1);
  const hidden = all.filter((l) => state.get(`shopHide:${l.slug}`) === 1);
  const byName = (a: ShoppingLine, b: ShoppingLine) => a.name.localeCompare(b.name);

  return {
    quantified: visible.filter((l) => l.qty !== null).sort(byName),
    unquantified: visible.filter((l) => l.qty === null).sort(byName),
    hidden: hidden.sort(byName),
    days: uniqueDays,
    people: n,
    mealCount,
    skippedCustom,
    cookedLines: visible.filter((l) => l.cooked && l.qty !== null).sort(byName),
  };
}

export function clampPeople(n: unknown): number {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return DEFAULT_PEOPLE;
  return Math.min(MAX_PEOPLE, Math.max(MIN_PEOPLE, v));
}

/** Reads the stored selection, falling back to a sensible default rather than empty. */
export function storedDays(state: PlanState, fallback: number[]): number[] {
  const raw = state.get<unknown>('setting:shopDays');
  if (!Array.isArray(raw)) return fallback;
  const days = raw.filter((d): d is number => Number.isInteger(d) && d >= 0 && d <= 111);
  return days.length ? [...new Set(days)].sort((a, b) => a - b) : fallback;
}

export function storedPeople(state: PlanState): number {
  return clampPeople(state.get<unknown>('setting:shopPeople') ?? DEFAULT_PEOPLE);
}

/** Display: "1980g", "15", "22.5ml". Trailing zeros are noise on a shopping list. */
export function formatQty(line: ShoppingLine): string {
  if (line.qty === null) return '';
  const n = Number.isInteger(line.qty) ? String(line.qty) : String(round(line.qty));
  return line.unit ? `${n}${line.unit === 'g' || line.unit === 'ml' ? '' : ' '}${line.unit}` : n;
}

function round(n: number): number {
  return Math.round(n * 10) / 10;
}

/** Every meal name in the selection, for the "what am I shopping for" summary. */
export function mealsInSelection(state: PlanState, days: readonly number[]): string[] {
  const out = new Set<string>();
  for (const d of days) for (const s of slotsFor(state, d) as SlotName[]) {
    if (!isCustom(state, d, s)) out.add(plannedMealFor(state, d, s).n);
  }
  return [...out].sort();
}

export { PLAN };
